/**
 * Normalises a Property Insights result (property_summary_api/CLIENT_GUIDE.md).
 *
 * The schema is wide — COPE plus a four-system check plus a region checklist —
 * and almost every field is optional: "any field may be null/empty if it wasn't
 * determinable from the photos". So this exists to make "absent" and "the model
 * looked and found nothing" different things at the view layer, because they
 * mean opposite things to a underwriter.
 *
 * NOT verified against a live response. The service is on an internal address
 * this workspace cannot reach, so unlike every other adapter here this one is
 * written from the documented schema. Treat the field names as the guide's
 * claim rather than as observed fact.
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

const text = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

/** "residential_house" -> "Residential house" */
export const humaniseValue = (value) => {
  const clean = text(value);
  if (!clean) return null;
  const words = clean.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
};

/**
 * Severity and condition, on the scale the app already draws.
 *
 * The service says major/moderate/minor for damages and
 * excellent…severe for condition; both map onto the three severities the rest
 * of the UI colours, so a finding here looks like a finding anywhere else.
 */
export const severityOf = (value) => {
  switch (text(value)?.toLowerCase()) {
    case "major":
    case "severe":
      return "severe";
    case "moderate":
    case "poor":
      return "moderate";
    case "minor":
    case "fair":
      return "minor";
    default:
      return null;
  }
};

const CONDITION_TONE = {
  excellent: "good",
  good: "good",
  fair: "warn",
  poor: "bad",
  severe: "bad",
};

/**
 * `evidence_images` are 1-based indexes into the upload order.
 *
 * Only meaningful because this app controls that order — it uploads the
 * photographs in the order the page lays them out. Resolved to the photographs
 * themselves so a finding can be clicked through to what it was seen in;
 * an index pointing past the end is dropped rather than rendered as a gap.
 */
export const resolveEvidence = (indexes, photos = []) =>
  asArray(indexes)
    .map((index) => Number(index))
    .filter((index) => Number.isInteger(index) && index >= 1 && index <= photos.length)
    .map((index) => photos[index - 1]);

/**
 * `not_shown` is not "absent".
 *
 * The guide is emphatic: for a system's condition and a protection measure's
 * status it means the item did not appear in any photograph, so no assessment
 * was made — "a house with heating: not_shown almost certainly has heating; it
 * just wasn't photographed". Rendering that as "no heating" would invert it, so
 * the flag travels with the value.
 */
const NOT_ASSESSED = new Set(["not_shown", "not_assessed", "unclear"]);
const isNotAssessed = (value) => NOT_ASSESSED.has(text(value)?.toLowerCase());

export const toPropertySummary = (result, photos = []) => {
  if (!result || typeof result !== "object") return null;

  const condition = result.overall_condition || {};
  const conditionRating = text(condition.rating)?.toLowerCase() || null;

  return {
    propertyType: humaniseValue(result.property_type),
    imageCount: Number(result.image_count) || photos.length || 0,
    narrative: text(result.narrative),

    condition: {
      rating: conditionRating,
      label: humaniseValue(condition.rating),
      tone: (conditionRating && CONDITION_TONE[conditionRating]) || "muted",
      explanation: text(condition.explanation),
    },

    construction: {
      wallMaterial: humaniseValue(result.construction?.wall_material),
      roofMaterial: humaniseValue(result.construction?.roof_material),
      stories: Number.isFinite(Number(result.construction?.stories))
        ? Number(result.construction.stories)
        : null,
      era: text(result.construction?.estimated_era_or_age),
      notes: text(result.construction?.notes),
    },

    occupancy: {
      status: humaniseValue(result.occupancy?.status),
      type: humaniseValue(result.occupancy?.occupancy_type),
      businessUse: result.occupancy?.business_use_visible === true,
      notes: text(result.occupancy?.notes),
    },

    // Roof, heating, electrical, plumbing — each either assessed or explicitly
    // not photographed, never quietly missing.
    systems: Object.entries(result.systems || {}).map(([key, system]) => ({
      key,
      label: humaniseValue(key),
      seen: system?.seen === true,
      assessed: !isNotAssessed(system?.condition),
      condition: humaniseValue(system?.condition),
      severity: severityOf(system?.condition),
      observations: text(system?.observations),
      evidence: resolveEvidence(system?.evidence_images, photos),
    })),

    damages: asArray(result.visible_damages).map((damage, index) => ({
      id: `damage-${index}`,
      element: humaniseValue(damage?.element) || "Unspecified",
      description: text(damage?.description),
      severity: severityOf(damage?.severity),
      severityLabel: humaniseValue(damage?.severity),
      evidence: resolveEvidence(damage?.evidence_images, photos),
    })),

    risks: asArray(result.risk_factors).map((risk, index) => ({
      id: `risk-${index}`,
      factor: text(risk?.factor) || "Unspecified",
      category: humaniseValue(risk?.category),
    })),

    protection: asArray(result.protection).map((item, index) => ({
      id: `protection-${index}`,
      measure: humaniseValue(item?.measure) || "Measure",
      present: text(item?.status)?.toLowerCase() === "present",
      assessed: !isNotAssessed(item?.status),
      notes: text(item?.notes),
      evidence: resolveEvidence(item?.evidence_images, photos),
    })),

    hazards: asArray(result.liability_hazards).map((hazard, index) => ({
      id: `hazard-${index}`,
      hazard: text(hazard?.hazard) || "Hazard",
      description: text(hazard?.description),
      evidence: resolveEvidence(hazard?.evidence_images, photos),
    })),

    recommendations: asArray(result.recommendations).map(text).filter(Boolean),

    // What the photographs did not cover is a request for more photographs,
    // which is actionable in a way the rest of the summary is not.
    coverage: {
      covered: asArray(result.photo_coverage?.areas_covered).map(text).filter(Boolean),
      notCovered: asArray(result.photo_coverage?.areas_not_covered)
        .map(text)
        .filter(Boolean),
    },

    region: {
      key: text(result.region_profile?.key),
      name: text(result.region_profile?.name),
    },

    /**
     * The region checklist, with what it could not judge separated out.
     *
     * High-priority items get a dedicated per-image pass, so a "high" item
     * marked absent carries more weight than a normal one — worth surfacing
     * rather than flattening into one list.
     */
    checklist: asArray(result.checklist).map((item, index) => ({
      id: text(item?.key) || `check-${index}`,
      label: text(item?.label) || humaniseValue(item?.key) || "Item",
      category: humaniseValue(item?.category),
      high: text(item?.priority)?.toLowerCase() === "high",
      status: text(item?.status)?.toLowerCase() || null,
      assessed: !isNotAssessed(item?.status),
      present: text(item?.status)?.toLowerCase() === "present",
      notes: text(item?.notes),
      evidence: resolveEvidence(item?.evidence_images, photos),
    })),

    inventory: asArray(result.image_inventory).map((entry) => ({
      index: Number(entry?.image) || null,
      label: text(entry?.label),
      photo: resolveEvidence([entry?.image], photos)[0] || null,
    })),

    // Batches that failed, images too dark, and so on. Shown, because a
    // summary drawn from a partial read should say so.
    limitations: asArray(result.limitations).map(text).filter(Boolean),
  };
};

export default toPropertySummary;
